import axios from "axios";
import PropertiesApi from "./api/propertiesApi";
import { getMediaUseS3 } from "../utils/mediaUrl";

const CHUNK_SIZE = 5 * 1024 * 1024;

function fileExtension(fileName) {
  const parts = fileName?.split(".") || [];
  return parts.length > 1 ? parts.pop().toLowerCase() : "bin";
}

function uniqueAttachmentName(file) {
  const ext = fileExtension(file.name);
  return `attachment-${crypto.randomUUID()}.${ext}`;
}

async function uploadViaPresignedMultipart(file, { onProgress, shouldAbort }) {
  const uniqueFileName = uniqueAttachmentName(file);
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadId;
  const parts = [];

  for (let i = 0; i < totalChunks; i++) {
    if (shouldAbort?.()) {
      throw new Error("Upload aborted");
    }

    const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const formData = {
      fileName: uniqueFileName,
      chunkIndex: i,
      file_type: file.type,
    };
    if (uploadId) {
      formData.uploadId = uploadId;
    }

    const presignedResponse = await PropertiesApi.getPresignedURLs(formData);
    const presignedData = presignedResponse?.data ?? presignedResponse;
    const presignedUrl = presignedData?.presignedUrl;
    uploadId = uploadId || presignedData?.uploadId;

    if (!presignedUrl) {
      throw new Error("Presigned upload URL missing from server.");
    }

    const putResponse = await axios.put(presignedUrl, chunk, {
      headers: { "Content-Type": "application/octet-stream" },
    });

    parts.push({
      PartNumber: i + 1,
      ETag: putResponse.headers.etag,
    });

    if (onProgress) {
      onProgress(Math.round((parts.length / totalChunks) * 100));
    }
  }

  const finalizeResponse = await PropertiesApi.finalizeUpload({
    fileName: uniqueFileName,
    uploadId,
    parts,
  });
  const finalizeData = finalizeResponse?.data ?? finalizeResponse;
  const fileUrl = finalizeData?.fileUrl ?? finalizeData?.file_url;

  if (fileUrl && String(fileUrl).includes("app_data/")) {
    const match = String(fileUrl).match(/app_data\/user\/property\/media\/[^?#]+/);
    if (match) {
      return match[0];
    }
  }

  return `app_data/user/property/media/${uniqueFileName}`;
}

async function uploadViaServer(file, { onProgress }) {
  const response = await PropertiesApi.uploadPropertyMedia(file, (progress) => {
    if (onProgress) {
      onProgress(Math.round(progress * 100));
    }
  });
  const inner = response?.data ?? response;
  const relative = inner?.file;
  if (!relative) {
    throw new Error("Property media upload did not return a file path.");
  }
  return relative;
}

/**
 * Upload property listing files; returns relative paths for listing-save media[].
 */
export async function uploadPropertyMediaFiles(files, options = {}) {
  const { onProgress, shouldAbort } = options;
  const list = Array.from(files || []);
  if (list.length === 0) {
    return [];
  }

  const uploaded = [];
  const useS3 = getMediaUseS3();

  for (let index = 0; index < list.length; index++) {
    if (shouldAbort?.()) {
      break;
    }
    const file = list[index];
    const relative = useS3
      ? await uploadViaPresignedMultipart(file, {
          onProgress,
          shouldAbort,
        })
      : await uploadViaServer(file, { onProgress });
    uploaded.push(relative);
    if (onProgress) {
      onProgress(Math.round(((index + 1) / list.length) * 100));
    }
  }

  return uploaded;
}
